"use client"

import { useState } from "react"
import { useLanguage } from "../../context/LanguageContext"
import "./ParameterSelectionModal.css"

const ParameterSelectionModal = ({ onClose, onConfirm }) => {
  const { t } = useLanguage()
  const [parameters, setParameters] = useState({
    temperature: {
      selected: true,
      fields: {
        tmax: true,
        ta: true,
        tmin: true,
        stationFetch: true,
      },
    },
    humidity: {
      selected: true,
      fields: {
        rhmax: true,
        rha: true,
        rhmin: true,
        uz: true,
        ud: true,
        un: true,
        zw: true,
      },
    },
    windSpeed: {
      selected: true,
      fields: {
        zp: true,
      },
    },
    radiation: {
      selected: true,
      fields: {
        n: true,
        rs: true,
        rn: true,
        epan: true,
      },
    },
  })

  const handleCategoryChange = (category) => {
    setParameters((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        selected: !prev[category].selected,
        fields: Object.keys(prev[category].fields).reduce((acc, field) => {
          acc[field] = !prev[category].selected
          return acc
        }, {}),
      },
    }))
  }

  const handleFieldChange = (category, field) => {
    setParameters((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        fields: {
          ...prev[category].fields,
          [field]: !prev[category].fields[field],
        },
      },
    }))

    // Update category selection based on fields
    const allFieldsDeselected = Object.values({
      ...parameters[category].fields,
      [field]: !parameters[category].fields[field],
    }).every((value) => !value)

    if (allFieldsDeselected) {
      setParameters((prev) => ({
        ...prev,
        [category]: {
          ...prev[category],
          selected: false,
        },
      }))
    } else {
      setParameters((prev) => ({
        ...prev,
        [category]: {
          ...prev[category],
          selected: true,
        },
      }))
    }
  }

  const handleConfirm = () => {
    // Ensure at least one parameter is selected
    const hasSelection = Object.values(parameters).some(
      (category) => category.selected && Object.values(category.fields).some((field) => field),
    )

    if (!hasSelection) {
      alert(t("Please select at least one parameter"))
      return
    }

    onConfirm(parameters)
  }

  return (
    <div className="parameter-modal-overlay">
      <div className="parameter-modal-container">
        <div className="parameter-modal-header">
          <h3>{t("Select Parameters for Template")}</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="parameter-modal-content">
          <p className="parameter-description">
            {t("Select the parameters you want to include in your weather data template:")}
          </p>

          <div className="parameter-categories">
            {/* Temperature Category */}
            <div className="parameter-category">
              <label className="parameter-category-checkbox">
                <input
                  type="checkbox"
                  checked={parameters.temperature.selected}
                  onChange={() => handleCategoryChange("temperature")}
                />
                <span className="category-title">{t("Temperature")}</span>
              </label>

              {parameters.temperature.selected && (
                <div className="parameter-fields">
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.temperature.fields.tmax}
                      onChange={() => handleFieldChange("temperature", "tmax")}
                    />
                    <span>{t("Tmax (°C)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.temperature.fields.ta}
                      onChange={() => handleFieldChange("temperature", "ta")}
                    />
                    <span>{t("Ta (°C)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.temperature.fields.tmin}
                      onChange={() => handleFieldChange("temperature", "tmin")}
                    />
                    <span>{t("Tmin (°C)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.temperature.fields.stationFetch}
                      onChange={() => handleFieldChange("temperature", "stationFetch")}
                    />
                    <span>{t("Station fetch (m)")}</span>
                  </label>
                </div>
              )}
            </div>

            {/* Humidity Category */}
            <div className="parameter-category">
              <label className="parameter-category-checkbox">
                <input
                  type="checkbox"
                  checked={parameters.humidity.selected}
                  onChange={() => handleCategoryChange("humidity")}
                />
                <span className="category-title">{t("Relative Humidity")}</span>
              </label>

              {parameters.humidity.selected && (
                <div className="parameter-fields">
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.humidity.fields.rhmax}
                      onChange={() => handleFieldChange("humidity", "rhmax")}
                    />
                    <span>{t("RHmax (%)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.humidity.fields.rha}
                      onChange={() => handleFieldChange("humidity", "rha")}
                    />
                    <span>{t("RHa (%)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.humidity.fields.rhmin}
                      onChange={() => handleFieldChange("humidity", "rhmin")}
                    />
                    <span>{t("RHmin (%)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.humidity.fields.uz}
                      onChange={() => handleFieldChange("humidity", "uz")}
                    />
                    <span>{t("Uz (m/s)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.humidity.fields.ud}
                      onChange={() => handleFieldChange("humidity", "ud")}
                    />
                    <span>{t("Ud (m/s)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.humidity.fields.un}
                      onChange={() => handleFieldChange("humidity", "un")}
                    />
                    <span>{t("Un (m/s)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.humidity.fields.zw}
                      onChange={() => handleFieldChange("humidity", "zw")}
                    />
                    <span>{t("Zw (m)")}</span>
                  </label>
                </div>
              )}
            </div>

            {/* Wind Speed Category */}
            <div className="parameter-category">
              <label className="parameter-category-checkbox">
                <input
                  type="checkbox"
                  checked={parameters.windSpeed.selected}
                  onChange={() => handleCategoryChange("windSpeed")}
                />
                <span className="category-title">{t("Wind Speed")}</span>
              </label>

              {parameters.windSpeed.selected && (
                <div className="parameter-fields">
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.windSpeed.fields.zp}
                      onChange={() => handleFieldChange("windSpeed", "zp")}
                    />
                    <span>{t("Zp")}</span>
                  </label>
                </div>
              )}
            </div>

            {/* Radiation Category */}
            <div className="parameter-category">
              <label className="parameter-category-checkbox">
                <input
                  type="checkbox"
                  checked={parameters.radiation.selected}
                  onChange={() => handleCategoryChange("radiation")}
                />
                <span className="category-title">{t("Radiation and Sunshine")}</span>
              </label>

              {parameters.radiation.selected && (
                <div className="parameter-fields">
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.radiation.fields.n}
                      onChange={() => handleFieldChange("radiation", "n")}
                    />
                    <span>{t("n (hour)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.radiation.fields.rs}
                      onChange={() => handleFieldChange("radiation", "rs")}
                    />
                    <span>{t("Rs (MJ/m².day)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.radiation.fields.rn}
                      onChange={() => handleFieldChange("radiation", "rn")}
                    />
                    <span>{t("Rn (MJ/m².day)")}</span>
                  </label>
                  <label className="parameter-field-checkbox">
                    <input
                      type="checkbox"
                      checked={parameters.radiation.fields.epan}
                      onChange={() => handleFieldChange("radiation", "epan")}
                    />
                    <span>{t("Epan (mm/day)")}</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <p className="parameter-note">
            {t("Note: Date will be included by default as it is essential for calculations.")}
          </p>
        </div>

        <div className="parameter-modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button className="confirm-btn" onClick={handleConfirm}>
            {t("Generate Template")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ParameterSelectionModal
